import { index, jsonb, pgTable, uuid, varchar } from 'drizzle-orm/pg-core';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import type { InvoiceHeaderAddress } from '@trashlab/api/src/domains/invoice/utils/invoice-header';
import type { InvoiceMemo } from '@trashlab/api/src/domains/invoice/utils/invoice-memos';
import { merchant_account } from '@trashlab/api/src/domains/billing/schemas/merchant-account';

export const hauler_location = pgTable(
  'hauler_location',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    merchantAccountId: uuid('merchantAccountId').references(
      () => merchant_account.id,
    ),

    // Basic information
    name: varchar('name', { length: 255 }).notNull(),

    // Invoice configuration
    /** DBA name to display on invoices for this branch */
    invoiceDbaName: varchar('invoiceDbaName', { length: 255 }),
    invoiceDisplayName: varchar('invoiceDisplayName', { length: 255 }),
    invoiceLogoPath: varchar('invoiceLogoPath', { length: 2048 }),
    invoicePhoneNumber: varchar('invoicePhoneNumber', { length: 32 }),
    invoiceEmail: varchar('invoiceEmail', { length: 256 }),
    /** Invoice From address; its domain must be a verified Email domain of the tenant */
    invoiceSenderEmail: varchar('invoiceSenderEmail', { length: 256 }),
    invoiceAddress: jsonb('invoiceAddress').$type<InvoiceHeaderAddress>(),
    /** Replaces the company invoice memos when set */
    invoiceMemos: jsonb('invoiceMemos').$type<InvoiceMemo[]>(),
  },
  (table) => {
    return {
      // Standard indexes
      createdAtTs_idx: index('hauler_location_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('hauler_location_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('hauler_location_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_desc_idx: index('hauler_location_updatedAtTs_desc_idx').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('hauler_location_versionNumber_idx').on(
        table.versionNumber,
      ),

      // Specific indexes
      name_idx: index('hauler_location_name_idx').on(table.name),
      tenant_merchant_account_idx: index(
        'hauler_location_tenant_merchant_account_idx',
      ).on(table.tenantId, table.merchantAccountId),
    };
  },
);

export const hauler_location_history = pgTable('hauler_location_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  merchantAccountId: uuid('merchantAccountId'),

  // Basic information
  name: varchar('name', { length: 255 }),

  // Invoice configuration
  /** DBA name to display on invoices for this branch */
  invoiceDbaName: varchar('invoiceDbaName', { length: 255 }),
  invoiceDisplayName: varchar('invoiceDisplayName', { length: 255 }),
  invoiceLogoPath: varchar('invoiceLogoPath', { length: 2048 }),
  invoicePhoneNumber: varchar('invoicePhoneNumber', { length: 32 }),
  invoiceEmail: varchar('invoiceEmail', { length: 256 }),
  invoiceSenderEmail: varchar('invoiceSenderEmail', { length: 256 }),
  invoiceAddress: jsonb('invoiceAddress').$type<InvoiceHeaderAddress>(),
  invoiceMemos: jsonb('invoiceMemos').$type<InvoiceMemo[]>(),
});

export type HaulerLocationSchema = typeof hauler_location.$inferSelect;
export type HaulerLocationInsert = typeof hauler_location.$inferInsert;
export type HaulerLocationHistorySchema =
  typeof hauler_location_history.$inferSelect;
